import { useMemo } from "react";
import { FormField } from "@/v2/components/form/form-schema";
import { useApi, apiResolver } from "@/api/useApi";
import { InteractionAdditionalContextField } from "./InteractionAdditionalContext.model";

type GetInteractionAdditionalContextRequest = {
  topicId: number;
  topicReleasePolicyId: string;
};

export type GetInteractionAdditionalContextResponse = {
  fields: InteractionAdditionalContextField[];
};

export interface UpsertInteractionAdditionalContextFieldsRequest {
  topicId: number;
  topicReleasePolicyId: string;
  fields: Omit<InteractionAdditionalContextField, "id">[];
}

type DeleteInteractionAdditionalContextFieldsRequest = {
  topicId: number;
  topicReleasePolicyId: string;
};

type DeleteInteractionAdditionalContextFieldsResponse = {
  count: number;
};

type GetInteractionAdditionalContextFieldsOptionsRequest = {
  topicId: number;
  topicReleasePolicyId: string;
};

export type InteractionAdditionalContextFieldsOptionsResponse = {
  customFields: FormField[];
  enrichments: FormField[];
  basicTicketData: FormField[];
};

export function useInteractionAdditionalContextApi() {
  const { api } = useApi();

  return useMemo(
    () => ({
      get: async ({
        topicId,
        topicReleasePolicyId,
      }: GetInteractionAdditionalContextRequest) =>
        apiResolver<GetInteractionAdditionalContextResponse>(
          api.get(
            `/platform/interaction-additional-context/topics/${topicId}/release-policies/${topicReleasePolicyId}`
          )
        ),
      upsertFields: async ({
        topicId,
        topicReleasePolicyId,
        fields,
      }: UpsertInteractionAdditionalContextFieldsRequest) =>
        apiResolver<GetInteractionAdditionalContextResponse>(
          api.put(
            `/platform/interaction-additional-context/topics/${topicId}/release-policies/${topicReleasePolicyId}/fields`,
            { fields }
          )
        ),
      deleteFields: async ({
        topicId,
        topicReleasePolicyId,
      }: DeleteInteractionAdditionalContextFieldsRequest) =>
        apiResolver<DeleteInteractionAdditionalContextFieldsResponse>(
          api.delete(
            `/platform/interaction-additional-context/topics/${topicId}/release-policies/${topicReleasePolicyId}/fields`
          )
        ),
      getFieldsOptions: async ({
        topicId,
        topicReleasePolicyId,
      }: GetInteractionAdditionalContextFieldsOptionsRequest) =>
        apiResolver<InteractionAdditionalContextFieldsOptionsResponse>(
          api.get(
            `/platform/interaction-additional-context/topics/${topicId}/release-policies/${topicReleasePolicyId}/options`
          )
        ),
      getQuackChatFieldOptions: async () =>
        apiResolver<FormField[]>(
          api.get(`/platform/interaction-additional-context/options/quackchat`)
        ),
    }),
    [api]
  );
}
